'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'

export default function Post5Content() {
  return (
    <BlogPostLayout
      title="Microplastics in Cosmetics: 87% of Products Contain Them"
      author="MicroPlastics Team"
      date="July 15, 2025"
      readTime="10 min read"
      heroImage="/blog/app-home.png"
      heroImageAlt="MicroPlastics app home screen showing product categories"
    >
      <BlogTLDR>
        <strong>~87% of cosmetic products from major brands contain
        microplastics or microplastic-derived polymers.</strong> The
        ingredient labels read like a chemistry textbook,{' '}
        <strong>polyethylene, acrylates copolymer, carbomer, PEG,
        dimethicone, polyurethane</strong>, and most consumers do not
        recognize these as plastics. The EU restricted intentionally added
        microplastics in cosmetics from October 2023; the US still allows
        them in leave-on products. Read ingredient lists; choose brands that
        certify plastic-free.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>~87% of mainstream cosmetic products contain microplastics or microplastic-derived liquid polymers.</>,
          <>Ingredient names that mean plastic: polyethylene, polypropylene, polyethylene terephthalate (PET), acrylates copolymer, polyurethane, polyquaternium, carbomer, polyacrylate, PEG, dimethicone.</>,
          <>EU restricted intentionally added microplastics in cosmetics from October 2023 with deadlines through 2035.</>,
          <>US still allows liquid polymers (PEG, carbomer, acrylates) in leave-on cosmetics, read labels and choose certified clean brands.</>,
          <>Swap these first: toothpaste, lip balm/lipstick, shampoo, body lotion, the products applied most often.</>,
          <>The US microbead ban covers <em>rinse-off only</em>; leave-on products (moisturiser, makeup, sunscreen) were never in scope. EU rinse-off compliance lands 2027, some leave-on not until 2035.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        87% of Your Cosmetics Likely Contain Microplastics
      </h2>
      <p>
        Every morning, millions of people apply moisturizer, foundation, sunscreen, and lip balm without
        a second thought. But a groundbreaking study by the{' '}
        <strong>Plastic Soup Foundation</strong> revealed a staggering reality: <strong>87% of cosmetic
        products from major global brands contain microplastics or synthetic polymers</strong>. That
        means nearly nine out of every ten beauty products sitting on your bathroom shelf are depositing
        tiny plastic particles onto your skin, into your pores, and eventually into the environment.
      </p>
      <p>
        The cosmetics industry has quietly relied on microplastics for decades. These microscopic
        plastic particles (typically smaller than 5 millimeters) serve as exfoliants,
        emulsifiers, film-forming agents, and texture enhancers. They make your foundation smoother,
        your scrub grittier, and your moisturizer silkier. But the hidden cost is enormous: these
        particles persist in the environment for hundreds of years, accumulate in marine ecosystems,
        and are increasingly linked to human health concerns including endocrine disruption and
        cellular inflammation.
      </p>
      <p>
        In this comprehensive guide, we break down exactly which microplastic ingredients lurk in your
        cosmetics, which product categories are the worst offenders, what health risks researchers have
        identified, how the EU is responding with landmark regulation, and (most importantly) how you can protect yourself starting today.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026 Regulatory Reality: The Gap Between Rinse-Off and Leave-On
      </h2>
      <p>
        The most useful thing to understand about cosmetics is that the regulation
        most people think protects them covers only a fraction of the products they use.
      </p>
      <p>
        In the United States, the <strong>Microbead-Free Waters Act of 2015</strong> banned
        plastic microbeads in <em>rinse-off</em> products -- face scrubs, toothpaste. It does{' '}
        <strong>not</strong> cover <em>leave-on</em> products: moisturisers, makeup, sunscreen
        and most other personal care. Those are precisely the products that sit on skin for
        hours rather than seconds, and they were never in scope.
      </p>
      <p>
        The EU is closing that gap, but slowly. Under the REACH restriction on intentionally
        added synthetic polymer microparticles, <strong>rinse-off products must be compliant
        by 2027</strong>, while certain <strong>leave-on cosmetics are not phased out until
        2035</strong>. In 2026 the Commission published Regulation (EU) 2026/1168 clarifying
        the scope of derogations under that entry. The practical upshot for a shopper in 2026:
        the reformulation wave is genuinely underway, but a compliant-today leave-on product
        may still contain polymer microparticles for years.
      </p>
      <p>
        Which is why label-reading remains the only reliable defence. Over{' '}
        <strong>90% of the polymers used in cosmetics are polyethylene</strong>, with
        polypropylene and polymethyl methacrylate behind it. If <strong>polyethylene</strong>{' '}
        or <strong>polypropylene</strong> appears in an ingredient list, the product contains
        microplastic -- regardless of what the front of the pack claims, and regardless of
        which regulation supposedly applies.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Are Microplastics in Cosmetics?
      </h2>
      <p>
        Microplastics in cosmetics fall into two broad categories: <strong>primary microplastics</strong>,
        which are intentionally added during manufacturing, and <strong>secondary microplastics</strong>,
        which form when larger plastic packaging degrades. The concern with cosmetics is primarily about
        intentionally added microplastics, synthetic polymers that companies deliberately include
        as functional ingredients.
      </p>
      <p>
        Unlike the visible plastic in packaging, these particles are often invisible to the naked eye.
        They can be solid beads, liquid polymers, or semi-solid gels. The Plastic Soup Foundation&apos;s
        &ldquo;Beat the Microbead&rdquo; campaign analyzed over 7,000 products and found that many
        well-known brands use dozens of different synthetic polymers across their product lines. Some
        individual products contained as many as <strong>10 to 15 different plastic ingredients</strong>.
      </p>
      <p>
        The European Chemicals Agency (ECHA) estimates that approximately <strong>42,000 tonnes of
        intentionally added microplastics</strong> are released into the environment in the EU alone
        each year, with cosmetics and personal care products contributing a significant share of that total.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 6 Most Common Microplastic Ingredients in Cosmetics
      </h2>
      <p>
        Learning to identify microplastics on ingredient labels does more to reduce your exposure
        than anything else you can do. Here are the six most prevalent synthetic polymers found in beauty
        and personal care products:
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        1. Polyethylene (PE)
      </h3>
      <p>
        Polyethylene is the most widely used plastic in the world, and it is also the most common
        microplastic found in cosmetics. In beauty products, PE appears as tiny beads in exfoliating
        scrubs, as binding agents in pressed powders, and as film-forming agents in mascaras and
        eyeliners. Before the US Microbead-Free Waters Act of 2015, polyethylene microbeads were the
        primary exfoliant in hundreds of facial scrubs. While solid microbeads have been restricted
        in rinse-off products in some countries, PE in other forms, such as waxes and
        emulsifiers, remains widespread. Look for <strong>&ldquo;Polyethylene&rdquo;</strong> or{' '}
        <strong>&ldquo;PE&rdquo;</strong> on ingredient labels.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        2. Polypropylene (PP)
      </h3>
      <p>
        Polypropylene is commonly used as a bulking agent and texture enhancer in lipsticks,
        foundations, and eye shadows. It helps create the smooth, spreadable consistency that
        consumers expect from high-end cosmetics. PP microparticles are also found in some
        exfoliating products as alternatives to the now-restricted PE microbeads. Because PP
        is chemically inert, manufacturers consider it &ldquo;safe&rdquo;, but its
        environmental persistence and potential to carry adsorbed pollutants tell a different story.
        Check labels for <strong>&ldquo;Polypropylene&rdquo;</strong> or <strong>&ldquo;PP&rdquo;</strong>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        3. Polymethyl Methacrylate (PMMA)
      </h3>
      <p>
        PMMA is a transparent thermoplastic often sold under trade names like Plexiglas. In cosmetics,
        it appears as microspheres that create a soft-focus, blurring effect on the skin. This makes
        it incredibly popular in anti-aging creams, primers, and foundations marketed as
        &ldquo;pore-minimizing&rdquo; or &ldquo;line-smoothing.&rdquo; A 2020 study published
        in <em>Environmental Science &amp; Technology</em> found PMMA particles in 62% of the
        foundation products tested. Look for <strong>&ldquo;Polymethyl Methacrylate&rdquo;</strong> or{' '}
        <strong>&ldquo;PMMA&rdquo;</strong> in the ingredients list.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        4. Nylon (Polyamide)
      </h3>
      <p>
        Nylon microparticles are used extensively in color cosmetics. They appear in eyeshadows,
        blushes, bronzers, and setting powders as bulking and opacifying agents. Nylon-12, in
        particular, is a favorite of cosmetic formulators because it creates a silky, luxurious
        feel and helps powders adhere to the skin. It also appears in mascara formulations to add
        volume and length. Research from the University of Plymouth found that a single application
        of powder makeup containing nylon can release <strong>thousands of microplastic particles</strong>.
        Watch for <strong>&ldquo;Nylon-6&rdquo;</strong>, <strong>&ldquo;Nylon-12&rdquo;</strong>, or{' '}
        <strong>&ldquo;Polyamide&rdquo;</strong> on labels.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        5. Polyethylene Terephthalate (PET)
      </h3>
      <p>
        PET is the same plastic used to make water bottles, and in cosmetics it appears primarily
        as glitter and shimmer particles. Many nail polishes, body glitters, highlighters, and
        eyeshadows contain PET microparticles that create their sparkle. A study by researchers at
        Anglia Ruskin University demonstrated that cosmetic glitter has measurable negative effects
        on freshwater ecosystems, reducing chlorophyll content and root growth in aquatic plants
        within 36 days. The environmental impact is compounded by the fact that glitter particles
        are almost impossible to capture in wastewater treatment. Look for{' '}
        <strong>&ldquo;Polyethylene Terephthalate&rdquo;</strong> or <strong>&ldquo;PET&rdquo;</strong>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        6. Polyurethane (PUR)
      </h3>
      <p>
        Polyurethane functions as a film-forming agent in many cosmetic products. It creates the
        long-lasting, waterproof barrier in products like setting sprays, waterproof mascaras, and
        long-wear foundations. It is also found in some nail polishes where it contributes to chip
        resistance. Because polyurethane forms a persistent film on the skin, there are growing
        concerns about its potential to trap other chemicals against the skin surface and increase
        transdermal absorption of other cosmetic ingredients. Check for{' '}
        <strong>&ldquo;Polyurethane&rdquo;</strong> or <strong>&ldquo;PUR&rdquo;</strong>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Which Cosmetic Categories Are Worst Offenders?
      </h2>
      <p>
        Microplastics are not limited to one type of product. They have infiltrated virtually
        every category of personal care. Here is a breakdown of where they are most prevalent:
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Skincare Products
      </h3>
      <p>
        Facial moisturizers, serums, anti-aging creams, and eye creams are among the most
        heavily contaminated categories. The Plastic Soup Foundation found that{' '}
        <strong>over 90% of skincare products from the top 10 global brands</strong> contained
        at least one synthetic polymer. Microplastics serve as emulsifiers (keeping oil and water
        mixed), viscosity regulators (controlling thickness), and film-forming agents (creating
        a smooth barrier). Products labeled &ldquo;long-lasting,&rdquo; &ldquo;pore-filling,&rdquo;
        or &ldquo;smoothing&rdquo; are particularly likely to contain PMMA or silicone-based
        polymers.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Makeup and Color Cosmetics
      </h3>
      <p>
        Foundations, concealers, powders, eyeshadows, lipsticks, and mascaras frequently rely on
        nylon, PE, PP, and PMMA for texture, adhesion, and optical effects. A 2022 study by the
        Dutch National Institute for Public Health found that a typical makeup routine involving
        foundation, concealer, powder, eyeshadow, and mascara could expose the wearer to{' '}
        <strong>over 30 different synthetic polymers</strong> in a single application.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Sunscreen
      </h3>
      <p>
        Many chemical sunscreens use synthetic polymers to improve the even distribution of UV
        filters on the skin and make them more water resistant. Acrylates copolymer and acrylates/C10-30
        alkyl acrylate crosspolymer are among the most common. These polymers wash off during
        swimming and bathing, contributing directly to marine microplastic pollution. Research
        published in <em>Marine Pollution Bulletin</em> estimated that recreational beach use
        releases approximately <strong>14,000 tonnes of sunscreen</strong> into ocean waters
        annually, with a significant portion containing synthetic polymer ingredients.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Shampoo and Hair Care
      </h3>
      <p>
        Shampoos, conditioners, hair masks, and styling products use microplastics for conditioning,
        detangling, and hold. Dimethicone (a silicone polymer), polyquaternium compounds, and
        acrylates copolymers are ubiquitous in hair care formulations. Because these are rinse-off
        products, the microplastics they contain flow directly into wastewater systems, where
        conventional treatment plants capture only <strong>65% to 92%</strong> of microplastic
        particles, allowing the rest to enter rivers and oceans.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Toothpaste
      </h3>
      <p>
        Before regulatory action, many leading toothpaste brands included polyethylene microbeads
        for a &ldquo;deep clean&rdquo; sensation. While solid PE beads have been banned in rinse-off
        products in the US, EU, UK, and several other jurisdictions, some toothpastes still
        contain other synthetic polymers like PEG (polyethylene glycol) derivatives and
        carbomer (a polyacrylic acid polymer) as thickeners and stabilizers. Dentists have
        reported finding microplastic particles <strong>embedded in patients&apos; gum tissue</strong>,
        raising concerns about chronic oral exposure.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Exfoliating Scrubs
      </h3>
      <p>
        Facial and body scrubs were historically the most visible source of microplastics in
        cosmetics. A single tube of exfoliating scrub could contain up to{' '}
        <strong>330,000 microbeads</strong>, according to research by the United Nations
        Environment Programme (UNEP). While legislation in many countries has targeted
        microbeads in rinse-off products, leave-on exfoliating treatments, such as
        chemical peels and overnight exfoliating masks, may still contain synthetic
        polymer particles that remain on the skin for extended periods.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Men&apos;s grooming: the same polymers, different bottles
      </h2>
      <p>
        This whole category gets read as a women&apos;s issue, and the
        chemistry does not care. The polymer families in men&apos;s grooming
        are identical &mdash; they are simply distributed across products
        nobody thinks of as cosmetics, which is why they go unaudited.
      </p>
      <p>Where they actually turn up:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Hair gel, wax and hairspray</strong> &mdash; acrylates copolymer and PVP/VP copolymer are the film formers doing the &ldquo;long hold&rdquo;.</li>
        <li><strong>Shave gel and foam</strong> &mdash; carbomer (Carbopol) is the thickener that makes it a gel rather than a liquid.</li>
        <li><strong>Body wash and face wash</strong> &mdash; PEGs, numbered (PEG-40 and similar), in almost every mass-market formula.</li>
        <li><strong>Dry shampoo and mattifying powders</strong> &mdash; nylon-6 and nylon-12 as texturisers.</li>
        <li><strong>Beard fillers and long-wear sunscreens</strong> &mdash; acrylates crosspolymer and polyurethane.</li>
        <li><strong>Exfoliating scrubs</strong> &mdash; older stock may still carry polyethylene or polypropylene beads.</li>
      </ul>
      <p>
        Two things worth separating out from the polymer question, because
        they travel with it in these products. <strong>Fragrance</strong> or{' '}
        <em>parfum</em> is an undisclosed mixture that frequently carries
        phthalates such as DEP as scent fixatives &mdash; that is a plasticiser
        issue rather than a particle one, and it is why heavily scented
        aftershave and body spray are worth a second look. And{' '}
        <strong>dimethicone</strong>, ubiquitous in men&apos;s moisturisers and
        conditioners, is a silicone: a synthetic polymer, but a liquid one, so
        it is not a microplastic under the solid-particle definition this page
        uses.
      </p>
      <p>
        The practical version: the two products worth checking first are the{' '}
        <strong>hair product you use daily</strong> and the{' '}
        <strong>body wash</strong>, because they are the highest-frequency
        contacts in most men&apos;s routines.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Health Risks: What Microplastics in Cosmetics Do to Your Body
      </h2>
      <p>
        The health implications of microplastic exposure through cosmetics are a growing area
        of scientific concern. While research is still evolving, several significant findings
        have emerged:
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Skin Absorption and Penetration
      </h3>
      <p>
        A 2023 study published in <em>Environment International</em> demonstrated that
        nanoplastics (particles smaller than 1 micrometer) can penetrate the outer layer
        of human skin, the stratum corneum, and reach the viable epidermis beneath. This
        is particularly concerning because cosmetics are designed to be applied directly to
        the skin, often on the face where skin is thinner and more permeable. Damaged or
        compromised skin, from conditions like eczema, acne, or even post-procedure
        sensitivity, may allow even larger microplastic particles to penetrate deeper
        layers.
      </p>
      <p>
        Once absorbed, microplastic particles can act as carriers for other harmful chemicals.
        Many synthetic polymers used in cosmetics are known to adsorb (bind to their surface)
        heavy metals, persistent organic pollutants, and other toxins. Research from the
        University of Birmingham found that microplastic particles recovered from cosmetic
        products carried concentrations of phthalates, parabens, and UV filters on their
        surfaces that were <strong>up to 1,000 times higher</strong> than the surrounding
        product matrix.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Endocrine Disruption
      </h3>
      <p>
        Perhaps the most alarming health concern is the link between microplastics and
        endocrine disruption. Many synthetic polymers contain or leach chemicals that
        mimic natural hormones, particularly estrogen. A landmark 2024 study published
        in the <em>New England Journal of Medicine</em> found that individuals with
        microplastics detected in their carotid artery plaque had a{' '}
        <strong>4.5-fold increased risk</strong> of heart attack, stroke, or death over
        a 34-month follow-up period compared to those without detectable microplastics.
      </p>
      <p>
        Bisphenol A (BPA) and phthalates, commonly associated with plastic manufacturing,
        are known endocrine disruptors that have been linked to reproductive issues, thyroid
        dysfunction, metabolic disorders, and increased cancer risk. When microplastic
        particles from cosmetics enter the body, they may release these chemicals slowly
        over time, creating chronic low-level exposure that accumulates throughout a
        person&apos;s lifetime. For a deeper dive into these health effects, read our guide
        on{' '}
        <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-semibold">
          microplastics and human health
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Cellular Inflammation and Oxidative Stress
      </h3>
      <p>
        Laboratory studies using human skin cell cultures have shown that exposure to
        microplastic particles triggers inflammatory responses, including increased
        production of pro-inflammatory cytokines like interleukin-6 (IL-6) and tumor
        necrosis factor alpha (TNF-alpha). Chronic inflammation is a well-established
        precursor to numerous diseases, including cardiovascular disease, autoimmune
        conditions, and cancer. A 2023 study in <em>Science of the Total Environment</em> found
        that polystyrene microplastics induced significant oxidative stress in human
        dermal fibroblasts, the cells responsible for producing collagen and maintaining
        skin structure. This suggests that microplastic exposure through cosmetics could
        paradoxically <strong>accelerate skin aging</strong>, the very condition
        many of these products claim to combat.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The EU Microplastics Ban: A Timeline for Change (2025&ndash;2035)
      </h2>
      <p>
        The European Union has enacted the most comprehensive regulatory response to
        microplastics in cosmetics through its restriction under REACH (Registration,
        Evaluation, Authorisation and Restriction of Chemicals). Here is the key
        timeline:
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>October 2023:</strong> The EU formally adopted the restriction on intentionally
          added microplastics under REACH Annex XVII. This covers synthetic polymer microparticles
          that are less than 5mm in any dimension, are organic, insoluble, and resist degradation.
        </li>
        <li>
          <strong>October 2025:</strong> Rinse-off cosmetic products containing microbeads (solid
          microplastic particles used for exfoliation) must be reformulated or removed from the
          EU market. This is the first enforcement deadline.
        </li>
        <li>
          <strong>October 2027:</strong> Leave-on cosmetic products, including moisturizers,
          foundations, serums, and sunscreens, must eliminate intentionally added
          microplastics. This is the most impactful deadline for the beauty industry as it
          affects the largest number of products.
        </li>
        <li>
          <strong>October 2029:</strong> Lip products, nail products, and makeup intended for
          use near mucous membranes must comply. These categories received an extended timeline
          due to reformulation complexity.
        </li>
        <li>
          <strong>October 2035:</strong> All remaining product categories, including those with
          the longest transition periods (such as certain encapsulated fragrances), must achieve
          full compliance. By this date, intentionally added microplastics in all cosmetic and
          personal care products sold in the EU will effectively be banned.
        </li>
      </ul>
      <p>
        The EU restriction is expected to prevent the release of approximately{' '}
        <strong>500,000 tonnes of microplastics</strong> into the environment over 20 years.
        It is already having a global ripple effect, as multinational brands reformulate products
        for EU compliance and apply those formulations worldwide. However, consumers in
        countries without similar regulation, including the United States, where no
        federal restriction on microplastics in leave-on cosmetics exists, must take
        personal responsibility for checking their products.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to Read Ingredient Labels for Hidden Microplastics
      </h2>
      <p>
        Identifying microplastics on cosmetic labels is challenging because they rarely appear
        as &ldquo;microplastics&rdquo; or &ldquo;plastic.&rdquo; Instead, they are listed by
        their chemical or polymer names, which can be difficult for consumers to recognize.
        Here is a practical guide:
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Red Flag Prefixes
      </h3>
      <p>
        Any ingredient beginning with <strong>&ldquo;Poly-&rdquo;</strong> should raise your
        awareness. While not all &ldquo;poly-&rdquo; ingredients are problematic (polysaccharides,
        for example, are natural), the prefix is the most reliable indicator of a synthetic
        polymer. Common examples include Polyethylene, Polypropylene, Polyurethane, Polyester,
        Polyamide, Polystyrene, and Polyacrylate.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Acrylate-Based Polymers
      </h3>
      <p>
        Ingredients containing <strong>&ldquo;acrylate&rdquo;</strong> or{' '}
        <strong>&ldquo;methacrylate&rdquo;</strong> are almost always synthetic polymers.
        Common examples include Acrylates Copolymer, Acrylates/C10-30 Alkyl Acrylate
        Crosspolymer, Ethyl Methacrylate, and Methyl Methacrylate Crosspolymer. These are
        extensively used in nail polishes, hair sprays, and long-wear makeup.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Silicone-Based Polymers
      </h3>
      <p>
        Ingredients ending in <strong>&ldquo;-cone&rdquo;</strong>,{' '}
        <strong>&ldquo;-conol&rdquo;</strong>, or <strong>&ldquo;-siloxane&rdquo;</strong> are
        silicone polymers. Dimethicone, Cyclomethicone, Cyclopentasiloxane, and Amodimethicone
        are among the most common. The classification of silicones as &ldquo;microplastics&rdquo;
        is debated (the EU restriction includes some but not all silicones) but
        many environmental and health advocates recommend avoiding them.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Other Names to Watch
      </h3>
      <p>
        Additional synthetic polymers commonly found in cosmetics include Carbomer
        (polyacrylic acid), PEG compounds (polyethylene glycol derivatives), VP/VA
        Copolymer (vinylpyrrolidone/vinyl acetate copolymer), and Styrene/Acrylates
        Copolymer. A comprehensive list from the Plastic Soup Foundation&apos;s Beat
        the Microbead campaign identifies over <strong>500 different synthetic polymer
        ingredients</strong> used in cosmetics.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the MicroPlastics App Scans Your Cosmetic Products
      </h2>
      <p>
        Memorizing hundreds of chemical names is unrealistic for most consumers. That is
        exactly why we built the MicroPlastics app. Here is how it simplifies the process
        of identifying microplastics in your cosmetics:
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Instant Barcode Scanning:</strong> Point your phone&apos;s camera at any
          product barcode, and the app immediately cross-references it against our database
          of over 100,000 products. Within seconds, you receive a clear microplastic risk
          score for that specific product.
        </li>
        <li>
          <strong>Ingredient Analysis:</strong> For products not yet in our database, you can
          photograph the ingredient label. Our AI-powered analysis engine identifies synthetic
          polymers and microplastic ingredients in the list, flagging each one with an
          explanation of what it is and why it is concerning.
        </li>
        <li>
          <strong>Product Categories:</strong> The app covers all major cosmetic and personal
          care categories, including skincare, makeup, hair care, sunscreen, oral care, and
          body care. Whether you are shopping for a new foundation or checking your existing
          shampoo, the app has you covered.
        </li>
        <li>
          <strong>Cleaner Alternatives:</strong> When a product scores poorly, the app suggests
          verified microplastic-free alternatives in the same category and price range. This
          makes it easy to switch without compromising on quality or spending hours researching.
        </li>
        <li>
          <strong>Exposure Tracking:</strong> The app tracks your cumulative microplastic
          exposure over time, helping you see the impact of switching to cleaner products.
          Many users report reducing their estimated exposure by over 70% within their first
          month.
        </li>
      </ul>
      <p>
        The free tier includes 5 product scans so you can start checking your most-used
        cosmetics immediately. For a more detailed guide on reducing your overall
        microplastic exposure, check out our article on{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:underline font-semibold">
          how to avoid microplastics in daily life
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Clean Beauty Alternatives: What to Use Instead
      </h2>
      <p>
        The good news is that the clean beauty movement has produced a growing number of
        high-quality products that perform just as well (or better) than
        their microplastic-laden counterparts. Here are practical alternatives for each
        product category:
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Exfoliants
      </h3>
      <p>
        Replace plastic microbead scrubs with products that use natural exfoliants such as
        jojoba beads, ground walnut shell, rice bran, bamboo powder, or fruit enzymes
        (papain from papaya, bromelain from pineapple). Chemical exfoliants containing
        AHA (glycolic acid, lactic acid) or BHA (salicylic acid) provide effective
        exfoliation without any particles at all.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Moisturizers and Serums
      </h3>
      <p>
        Seek products that use plant-based emollients and emulsifiers instead of synthetic
        polymers. Shea butter, jojoba oil, squalane (derived from olives or sugarcane),
        hyaluronic acid (which, despite sounding synthetic, is a naturally occurring
        molecule), and glycerin are all effective alternatives. Look for certifications
        like COSMOS Organic, NATRUE, or EWG Verified, which restrict or prohibit
        synthetic polymers.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Foundations and Makeup
      </h3>
      <p>
        Mineral makeup brands that use zinc oxide, titanium dioxide, iron oxides, and
        mica as their primary ingredients are generally free of synthetic polymers.
        Brands committed to clean formulations use kaolin clay, tapioca starch, and
        silica as alternatives to nylon and PMMA for texture and opacity.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Sunscreen
      </h3>
      <p>
        Mineral (physical) sunscreens that use zinc oxide or titanium dioxide as their
        active ingredients are less likely to contain synthetic polymers compared to
        chemical sunscreens. However, always check the inactive ingredients as well,
        since some mineral sunscreens still use acrylate copolymers for texture. The
        best options explicitly state &ldquo;reef-safe&rdquo; and
        &ldquo;microplastic-free&rdquo; on their packaging.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Hair Care
      </h3>
      <p>
        Replace silicone-based conditioners with products that use natural oils (argan,
        coconut, avocado) and plant-derived conditioning agents like cetyl alcohol (from
        coconut or palm) and behentrimonium methosulfate (a gentle, biodegradable
        conditioning agent). Solid shampoo and conditioner bars often have simpler,
        cleaner ingredient lists and dramatically reduce plastic packaging waste as well.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Toothpaste
      </h3>
      <p>
        Choose toothpastes that use baking soda, calcium carbonate, or hydrated silica
        as their abrasive agents. Many natural toothpaste brands have eliminated all
        synthetic polymers from their formulations. Toothpaste tablets are an emerging
        alternative that typically have very short, clean ingredient lists.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        5 Actionable Steps to Eliminate Microplastics From Your Beauty Routine
      </h2>
      <ol className="list-decimal pl-6 space-y-4">
        <li>
          <strong>Audit your current products.</strong> Start by scanning the products you
          use daily with the MicroPlastics app. Focus on the products that stay on your skin
          the longest (moisturizers, foundations, sunscreens, and serums) since these provide the most prolonged exposure.
        </li>
        <li>
          <strong>Prioritize leave-on products.</strong> If you cannot replace everything at
          once, prioritize leave-on products over rinse-off products. A moisturizer that sits
          on your face for 12 hours poses a greater personal exposure risk than a face wash
          that contacts your skin for 30 seconds.
        </li>
        <li>
          <strong>Learn the key red flags.</strong> You do not need to memorize 500 ingredients.
          Just remember: &ldquo;Poly-&rdquo; prefixes, &ldquo;-acrylate&rdquo; suffixes,
          &ldquo;-cone&rdquo; endings, and &ldquo;Nylon&rdquo; cover the vast majority of
          synthetic polymers in cosmetics.
        </li>
        <li>
          <strong>Choose certified clean brands.</strong> Look for third-party certifications
          like COSMOS, NATRUE, EWG Verified, or the Plastic Soup Foundation&apos;s &ldquo;Zero
          Plastic Inside&rdquo; logo. These certifications verify that products are free from
          intentionally added microplastics.
        </li>
        <li>
          <strong>Spread awareness.</strong> Share what you learn with friends and family. The
          Plastic Soup Foundation found that consumer pressure is the strongest
          driver of reformulation. When enough people demand microplastic-free products,
          brands respond.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bottom Line
      </h2>
      <p>
        The 87% statistic is not an abstract number. It represents the overwhelming
        majority of cosmetic products that millions of people apply to their skin every
        single day. Microplastics in cosmetics are not just an environmental issue, they are a personal health issue. With mounting evidence linking synthetic polymer
        exposure to endocrine disruption, inflammation, and cardiovascular risk, the case
        for switching to cleaner products has never been stronger.
      </p>
      <p>
        The regulatory landscape is shifting, with the EU leading the way toward
        comprehensive bans. But regulation moves slowly, and enforcement timelines
        stretch to 2035. You do not have to wait a decade to protect yourself. By
        learning to read ingredient labels, choosing certified clean alternatives,
        and using tools like the MicroPlastics app to make informed purchasing decisions,
        you can dramatically reduce your exposure starting today.
      </p>
      <p className="font-semibold text-[#1E6B47]">
        Download the MicroPlastics app and scan your cosmetics now. Your skin (and the planet) will thank you.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'How many cosmetics contain microplastics?',
            answer:
              'Around 87% of mainstream cosmetic products from major brands contain microplastics or microplastic-derived liquid polymers, according to multiple ingredient-list audits. The figure is higher for color cosmetics, sunscreens, and lotions; lower for fragrance and some shampoos.',
          },
          {
            question: 'What ingredient names mean plastic in cosmetics?',
            answer:
              'Polyethylene, polypropylene, polyethylene terephthalate (PET), acrylates copolymer, polyurethane, polyquaternium, carbomer, polyacrylate, PEG (polyethylene glycol), dimethicone, methicone. If the ingredient name has "poly" in it or ends in "-mer", it is almost certainly a synthetic polymer.',
          },
          {
            question: 'Are EU cosmetics microplastic-free?',
            answer:
              'Not entirely. The EU 2023 restriction bans intentionally added solid microplastic particles in cosmetics, but liquid polymers (which dry to form films on skin) and many transitional categories are still allowed under multi-year phase-outs running through 2035. EU products are cleaner than US equivalents on average, but ingredient-checking is still required.',
          },
          {
            question: 'Are microplastics in skincare absorbed through the skin?',
            answer:
              'Larger microplastic particles do not cross intact skin. However, the smaller plasticisers and additives that travel with them (BPA, phthalates, parabens, fragrance) are absorbed efficiently. So the skin-absorption question is really about the chemicals, not the particles themselves.',
          },
          {
            question: 'Are "natural" or "organic" cosmetics microplastic-free?',
            answer:
              'Not automatically. "Natural" and "organic" are loosely regulated terms. Verify by ingredient list: look for absence of polyethylene, acrylates copolymer, PEG, carbomer, dimethicone, and similar polymer names. Certifications like COSMOS, Ecocert, and "Zero Plastic Inside" are more reliable signals.',
          },
          {
            question: 'Which cosmetic categories are worst for microplastics?',
            answer:
              'Mascara, eyeliner, foundation, lipstick, hair styling products, sunscreen, and exfoliating scrubs (where banned microbeads have often been replaced by other polymers). The categories applied most often or to large skin areas matter more, toothpaste and body lotion are high-priority swaps.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Commission',
            year: 2026,
            title: 'Commission Regulation (EU) 2026/1168 amending REACH Annex XVII, Entry 78 (synthetic polymer microparticles)',
            publisher: 'European Commission',
            url: 'https://en.reach24h.com/news/industry-news/chemical/reach-microplastics-regulation-2026-1168',
          },
          {
            authors: 'European Chemicals Agency (ECHA)',
            year: 2023,
            title: 'Restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'US FDA / Microbead-Free Waters Act',
            year: 2015,
            title: 'The Microbead-Free Waters Act FAQs',
            publisher: 'US FDA',
            url: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs',
          },
          {
            authors: 'Rochester JR, Bolden AL',
            year: 2015,
            title: 'Bisphenol S and F: Systematic Review and Comparison of the Hormonal Activity of Bisphenol A Substitutes',
            publisher: 'Environmental Health Perspectives',
            url: 'https://ehp.niehs.nih.gov/doi/10.1289/ehp.1408989',
          },
          {
            authors: 'Plastic Soup Foundation / Beat the Microbead',
            year: 2024,
            title: 'Plastics in Personal Care Products: searchable database',
            publisher: 'Plastic Soup Foundation',
            url: 'https://www.beatthemicrobead.org/',
          },
          {
            authors: 'Environmental Working Group',
            year: 2024,
            title: 'EWG\'s Skin Deep Cosmetics Database',
            publisher: 'EWG',
            url: 'https://www.ewg.org/skindeep/',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
